import { Controller, useFormContext } from 'react-hook-form';
import { FormControl, Select, MenuItem, Typography, FormLabel } from '@mui/material';

import type { CustomStep, CustomStepType } from '@shared/patient-intake-status';
import { metadataConfig } from './metadataConfig.ts';
import type { CustomStepFormData } from './CustomStepCreator.tsx';

type MetadataComponentProps<TStep extends CustomStepType> = {
  type: TStep;
  value: CustomStep[TStep]['metadata'];
  onChange: (value: CustomStep[TStep]['metadata']) => void;
  error?: string;
};

const MetadataComponent = <TStep extends CustomStepType>({
  type,
  value,
  onChange,
  error,
}: MetadataComponentProps<TStep>) => {
  const Component = metadataConfig[type].component;

  if (!Component) {
    return null;
  }

  return <Component value={value} onChange={onChange} error={error} />;
};

const STEP_TYPES = {
  DOCUMENT_UPLOAD: {
    label: 'Document Upload',
    description: 'The patient will be tasked with uploading a document',
  },
  MESSAGE: {
    label: 'Message',
    description: 'The patient will be tasked with sending a message in a chat you choose',
  },
  DISCHARGE_LETTER_UPLOAD: {
    label: 'Discharge Letter Upload',
    description: 'The patient will be tasked with uploading their discharge letter',
  },
} satisfies Record<
  CustomStepType,
  {
    label: string;
    description: string;
  }
>;

export const MetadataFormController = () => {
  const {
    control,
    formState: { errors, isSubmitting },
    watch,
    setValue,
  } = useFormContext<CustomStepFormData>();

  const watchedType = watch('type');

  // Handle type change - update metadata when type changes
  const handleTypeChange = (newType: CustomStepType) => {
    setValue('type', newType);
    setValue('metadata', metadataConfig[newType].initialValue);
  };

  return (
    <>
      <Controller
        name="type"
        control={control}
        render={({ field }) => (
          <FormControl fullWidth margin="normal" error={!!errors.type}>
            <FormLabel component="legend" sx={{ mb: 0.5 }}>
              What type of step do you want to create?
            </FormLabel>
            <Select
              {...field}
              onChange={(e) => handleTypeChange(e.target.value as CustomStepType)}
              disabled={isSubmitting}
            >
              {Object.entries(STEP_TYPES).map(([value, { label, description }]) => (
                <MenuItem key={value} value={value} sx={{ gap: 0.5, justifyContent: 'space-between' }}>
                  <Typography variant="body2">{label}</Typography>
                  <Typography variant="caption">{description}</Typography>
                </MenuItem>
              ))}
            </Select>

            {errors.type && (
              <Typography variant="caption" color="error">
                {errors.type.message}
              </Typography>
            )}
          </FormControl>
        )}
      />

      <Controller
        name="metadata"
        control={control}
        render={({ field }) => (
          <MetadataComponent
            type={watchedType}
            value={field.value}
            onChange={field.onChange}
            error={errors.metadata?.message}
          />
        )}
      />
    </>
  );
};
